import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Tag, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import projectService from '@/services/project.service';
import { getEmployeesForDropdown } from '@/services/employee.service';
import taskService, { CreateTaskRequest } from '@/services/task.service';
import { ProjectApiResponse } from '@/types/models';

interface CreateTaskFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;
  onTaskCreated?: (task: any) => void;
}

interface TaskFormData {
  projectId: string;
  subject: string;
  description: string;
  startDate: string;
  dueDate: string;
  priority: string;
  assignees: string[];
  followers: string[];
  tags: string[];
  estimateHours: number;
  url: string;
  isPublic: boolean;
  isBillable: boolean;
  isVisibleToCustomer: boolean;
}

const PRIORITY_OPTIONS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];

interface EmployeeOption {
  id: string;
  name: string;
  initials: string;
  email?: string;
  employeeId?: string;
}

// Helper function to generate initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export function CreateTaskForm({ open, onOpenChange, projectId, onTaskCreated }: CreateTaskFormProps) {
  const [formData, setFormData] = useState<TaskFormData>({
    projectId: projectId || '',
    subject: '',
    description: '',
    startDate: '',
    dueDate: '',
    priority: 'Medium',
    assignees: [],
    followers: [],
    tags: [],
    estimateHours: 0,
    url: '',
    isPublic: false,
    isBillable: true,
    isVisibleToCustomer: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [followerSearch, setFollowerSearch] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showFollowerDropdown, setShowFollowerDropdown] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const followerDropdownRef = useRef<HTMLDivElement>(null);
  const [projects, setProjects] = useState<ProjectApiResponse[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);

  // Update form projectId when prop changes
  useEffect(() => {
    if (projectId) {
      setFormData(prev => ({ ...prev, projectId }));
    }
  }, [projectId]);

  // Fetch projects and employees when form opens
  useEffect(() => {
    if (open) {
      fetchProjects();
      fetchEmployees();
    }
  }, [open]);

  const fetchProjects = async () => {
    setIsLoadingProjects(true);
    try {
      const response = await projectService.getProjects({});
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await getEmployeesForDropdown();
      // Transform employees to include initials
      const employeesWithInitials: EmployeeOption[] = response.map((emp: any) => ({
        id: emp._id || emp.id,
        name: emp.name,
        initials: getInitials(emp.name),
        email: emp.email,
        employeeId: emp.employeeId
      }));
      setEmployees(employeesWithInitials);
    } catch (error) {
      console.error('Failed to load employees:', error);
      toast.error('Failed to load employees');
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target as Node)) {
        setShowAssigneeDropdown(false);
      }
      if (followerDropdownRef.current && !followerDropdownRef.current.contains(event.target as Node)) {
        setShowFollowerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (field: keyof TaskFormData, value: string | number | string[] | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAssigneeToggle = (assigneeId: string) => {
    setFormData(prev => {
      const updatedAssignees = prev.assignees.includes(assigneeId)
        ? prev.assignees.filter(id => id !== assigneeId)
        : [...prev.assignees, assigneeId];

      // Clear error if assignees are selected
      if (errors.assignees && updatedAssignees.length > 0) {
        setErrors(currentErrors => {
          const newErrors = { ...currentErrors };
          delete newErrors.assignees;
          return newErrors;
        });
      }

      return {
        ...prev,
        assignees: updatedAssignees
      };
    });
  };

  const handleFollowerToggle = (followerId: string) => {
    setFormData(prev => {
      const updatedFollowers = prev.followers.includes(followerId)
        ? prev.followers.filter(id => id !== followerId)
        : [...prev.followers, followerId];

      // Clear error if followers are selected
      if (errors.followers && updatedFollowers.length > 0) {
        setErrors(currentErrors => {
          const newErrors = { ...currentErrors };
          delete newErrors.followers;
          return newErrors;
        });
      }

      return {
        ...prev,
        followers: updatedFollowers
      };
    });
  };

  const removeAssignee = (assigneeId: string) => {
    setFormData(prev => {
      const updatedAssignees = prev.assignees.filter(id => id !== assigneeId);
      
      // Clear error if assignees are still selected after removal
      if (errors.assignees && updatedAssignees.length > 0) {
        setErrors(currentErrors => {
          const newErrors = { ...currentErrors };
          delete newErrors.assignees;
          return newErrors;
        });
      }

      return {
        ...prev,
        assignees: updatedAssignees
      };
    });
  };

  const removeFollower = (followerId: string) => {
    setFormData(prev => {
      const updatedFollowers = prev.followers.filter(id => id !== followerId);
      
      // Clear error if followers are still selected after removal
      if (errors.followers && updatedFollowers.length > 0) {
        setErrors(currentErrors => {
          const newErrors = { ...currentErrors };
          delete newErrors.followers;
          return newErrors;
        });
      }

      return {
        ...prev,
        followers: updatedFollowers
      };
    });
  };

  const getSelectedAssignees = () => {
    return employees.filter(assignee => formData.assignees.includes(assignee.id));
  };

  const getSelectedFollowers = () => {
    return employees.filter(follower => formData.followers.includes(follower.id));
  };

  const getFilteredAssignees = () => {
    return employees.filter(assignee =>
      assignee.name.toLowerCase().includes(assigneeSearch.toLowerCase())
    );
  };

  const getFilteredFollowers = () => {
    return employees.filter(follower =>
      follower.name.toLowerCase().includes(followerSearch.toLowerCase())
    );
  };

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.tags.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, trimmedTag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!projectId && !formData.projectId) {
      newErrors.projectId = 'Project is required';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.priority) {
      newErrors.priority = 'Priority is required';
    }
    if (!formData.assignees || formData.assignees.length === 0) {
      newErrors.assignees = 'Assignees is required';
    }
    if (!formData.followers || formData.followers.length === 0) {
      newErrors.followers = 'Followers is required';
    }
    if (!formData.estimateHours || formData.estimateHours <= 0) {
      newErrors.estimateHours = 'Estimate hours is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Task description is required';
    }

    // Validate date range
    if (formData.startDate && formData.dueDate && formData.startDate > formData.dueDate) {
      newErrors.dueDate = 'Due date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Prepare task data for API
      const taskData: CreateTaskRequest = {
        title: formData.subject,
        description: formData.description,
        projectId: formData.projectId,
        assignees: formData.assignees, // These are already user._id values from employees
        followers: formData.followers, // These are already user._id values from employees
        startDate: formData.startDate,
        dueDate: formData.dueDate || undefined,
        priority: formData.priority as 'Low' | 'Medium' | 'High',
        tags: formData.tags.length > 0 ? formData.tags : undefined,
        estimateHours: formData.estimateHours,
        url: formData.url || undefined,
      };

      // Call the API to create the task
      const createdTask = await taskService.createTask(taskData);

      toast.success("Task Created Successfully", {
        description: `"${formData.subject}" has been created and added to the project.`,
      });

      // Call the callback if provided with the created task data
      if (onTaskCreated) {
        // Transform API response to match the expected format for the UI
        const assignedUsers = employees.filter(user => formData.assignees.includes(user.id));
        const taskForUI = {
          id: createdTask._id,
          projectId: createdTask.projectId,
          name: createdTask.title,
          description: createdTask.description,
          status: createdTask.status || 'Not Started',
          priority: createdTask.priority,
          startDate: createdTask.startDate,
          dueDate: createdTask.dueDate,
          assignedTo: assignedUsers.length > 0 ? {
            name: assignedUsers[0].name,
            initials: assignedUsers[0].initials,
          } : {
            name: 'Unknown',
            initials: 'U',
          },
          assignedUsers: assignedUsers,
          followers: employees.filter(user => formData.followers.includes(user.id)),
          tags: createdTask.tags,
          estimatedHours: createdTask.estimateHours,
          url: createdTask.url,
        };
        onTaskCreated(taskForUI);
      }

      // Reset form
      setFormData({
        projectId: projectId || '',
        subject: '',
        description: '',
        startDate: '',
        dueDate: '',
        priority: 'Medium',
        assignees: [],
        followers: [],
        tags: [],
        estimateHours: 0,
        url: '',
        isPublic: false,
        isBillable: true,
        isVisibleToCustomer: true,
      });
      setAssigneeSearch('');
      setFollowerSearch('');
      setTagInput('');
      setShowAssigneeDropdown(false);
      setShowFollowerDropdown(false);
      setErrors({});
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error creating task:', error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to create task. Please try again.";
      toast.error("Error", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      projectId: projectId || '',
      subject: '',
      description: '',
      startDate: '',
      dueDate: '',
      priority: 'Medium',
      assignees: [],
      followers: [],
      tags: [],
      estimateHours: 0,
      url: '',
      isPublic: false,
      isBillable: true,
      isVisibleToCustomer: true,
    });
    setAssigneeSearch('');
    setFollowerSearch('');
    setTagInput('');
    setShowAssigneeDropdown(false);
    setShowFollowerDropdown(false);
    setErrors({});
    onOpenChange(false);
  };

  // Reset form when dialog closes
  const handleDialogClose = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset all form data
      setFormData({
        projectId: projectId || '',
        subject: '',
        description: '',
        startDate: '',
        dueDate: '',
        priority: 'Medium',
        assignees: [],
        followers: [],
        tags: [],
        estimateHours: 0,
        url: '',
        isPublic: false,
        isBillable: true,
        isVisibleToCustomer: true,
      });
      // Clear all errors
      setErrors({});
      // Reset all input states
      setAssigneeSearch('');
      setFollowerSearch('');
      setTagInput('');
      setShowAssigneeDropdown(false);
      setShowFollowerDropdown(false);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[rgb(209,209,209)] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:[display:none] [scrollbar-width:thin] [scrollbar-color:rgb(209,209,209)_transparent]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Create New Task</DialogTitle>
          <DialogDescription>
            Fill in the details below to create a new task for this project.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Checkboxes */}


          {/* Project Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Project *</Label>
            <Select
              value={formData.projectId}
              onValueChange={(value) => handleInputChange('projectId', value)}
              disabled={isLoadingProjects}
            >
              <SelectTrigger className={cn(errors.projectId && "border-red-500")}>
                <SelectValue placeholder={isLoadingProjects ? "Loading projects..." : "Select a project"} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project._id} value={project._id}>
                    {project.projectName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.projectId && (
              <p className="text-sm text-red-500">{errors.projectId}</p>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-sm font-medium">
              Subject *
            </Label>
            <Input
              id="subject"
              placeholder="Enter task subject"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              className={cn(errors.subject && "border-red-500")}
            />
            {errors.subject && (
              <p className="text-sm text-red-500">{errors.subject}</p>
            )}
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label htmlFor="startDate" className="text-sm font-medium">
              Start Date *
            </Label>
            <div className="relative">
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className={cn(errors.startDate && "border-red-500")}
              />
              <Calendar className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
            {errors.startDate && (
              <p className="text-sm text-red-500">{errors.startDate}</p>
            )}
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="dueDate" className="text-sm font-medium">
              Due Date
            </Label>
            <div className="relative">
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                className={cn(errors.dueDate && "border-red-500")}
              />
              <Calendar className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
            {errors.dueDate && (
              <p className="text-sm text-red-500">{errors.dueDate}</p>
            )}
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Priority *</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => handleInputChange('priority', value)}
            >
              <SelectTrigger className={cn(errors.priority && "border-red-500")}>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.priority && (
              <p className="text-sm text-red-500">{errors.priority}</p>
            )}
          </div>

          {/* Assignees */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Assignees *</Label>
            <div className="relative" ref={assigneeDropdownRef}>
              <div
                className={cn(
                  "min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex flex-wrap gap-1 items-center cursor-pointer",
                  errors.assignees && "border-red-500"
                )}
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
              >
                {getSelectedAssignees().length > 0 ? (
                  getSelectedAssignees().map((assignee) => (
                    <div
                      key={assignee.id}
                      className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                    >
                      <div className="h-4 w-4 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium">
                        {assignee.initials}
                      </div>
                      <span>{assignee.name}</span>
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-blue-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAssignee(assignee.id);
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground">Select assignees</span>
                )}
                <Search className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
              
              {showAssigneeDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search assignees..."
                      value={assigneeSearch}
                      onChange={(e) => setAssigneeSearch(e.target.value)}
                      className="h-8"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="py-1">
                    {getFilteredAssignees().length > 0 ? (
                      getFilteredAssignees().map((assignee) => (
                        <div
                          key={assignee.id}
                          className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssigneeToggle(assignee.id);
                          }}
                        >
                          <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800">
                            {assignee.initials}
                          </div>
                          <span className="text-sm">{assignee.name}</span>
                          {formData.assignees.includes(assignee.id) && (
                            <div className="ml-auto h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white" />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No assignees found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {errors.assignees && (
              <p className="text-sm text-red-500">{errors.assignees}</p>
            )}
          </div>

          {/* Followers */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Followers *</Label>
            <div className="relative" ref={followerDropdownRef}>
              <div
                className={cn(
                  "min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex flex-wrap gap-1 items-center cursor-pointer",
                  errors.followers && "border-red-500"
                )}
                onClick={() => setShowFollowerDropdown(!showFollowerDropdown)}
              >
                {getSelectedFollowers().length > 0 ? (
                  getSelectedFollowers().map((follower) => (
                    <div
                      key={follower.id}
                      className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs"
                    >
                      <div className="h-4 w-4 rounded-full bg-green-200 flex items-center justify-center text-xs font-medium">
                        {follower.initials}
                      </div>
                      <span>{follower.name}</span>
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-green-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFollower(follower.id);
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground">Select followers</span>
                )}
                <Search className="h-4 w-4 text-muted-foreground ml-auto" />
              </div>
              
              {showFollowerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2 border-b">
                    <Input
                      placeholder="Search followers..."
                      value={followerSearch}
                      onChange={(e) => setFollowerSearch(e.target.value)}
                      className="h-8"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="py-1">
                    {getFilteredFollowers().length > 0 ? (
                      getFilteredFollowers().map((follower) => (
                        <div
                          key={follower.id}
                          className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFollowerToggle(follower.id);
                          }}
                        >
                          <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-medium text-green-800">
                            {follower.initials}
                          </div>
                          <span className="text-sm">{follower.name}</span>
                          {formData.followers.includes(follower.id) && (
                            <div className="ml-auto h-4 w-4 rounded-full bg-green-600 flex items-center justify-center">
                              <div className="h-2 w-2 rounded-full bg-white" />
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No followers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {errors.followers && (
              <p className="text-sm text-red-500">{errors.followers}</p>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="tags" className="text-sm font-medium">
              Tags
            </Label>
            <div className="min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex flex-wrap gap-1 items-center">
              {formData.tags.map((tag, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-1 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs"
                >
                  <span>{tag}</span>
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-purple-600"
                    onClick={() => removeTag(tag)}
                  />
                </div>
              ))}
              <Input
                id="tags"
                placeholder="Add tags (press Enter or comma to add)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                className="flex-1 min-w-[200px] border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto"
              />
            </div>
          </div>

          {/* Estimate hour */}
          <div className="space-y-2">
            <Label htmlFor="estimateHours" className="text-sm font-medium">
              Estimate hour *
            </Label>
            <Input
              id="estimateHours"
              type="number"
              placeholder="Enter estimated hours"
              value={formData.estimateHours || ''}
              onChange={(e) => handleInputChange('estimateHours', Number(e.target.value) || 0)}
              min="0"
              step="0.5"
              className={cn(errors.estimateHours && "border-red-500")}
            />
            {errors.estimateHours && (
              <p className="text-sm text-red-500">{errors.estimateHours}</p>
            )}
          </div>

          {/* URL */}
          <div className="space-y-2">
            <Label htmlFor="url" className="text-sm font-medium">
              URL
            </Label>
            <Input
              id="url"
              placeholder="Enter URL"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              className={cn(errors.url && "border-red-500")}
            />
            {errors.url && (
              <p className="text-sm text-red-500">{errors.url}</p>
            )}
          </div>

          {/* Task Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">
              Task Description *
            </Label>
            <div className="relative">
              <Textarea
                id="description"
                placeholder="Add Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={cn("min-h-[100px]", errors.description && "border-red-500")}
              />
              <div className="absolute bottom-2 right-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          <DialogFooter className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Close
            </Button>
            <Button type="submit" className="bg-black hover:bg-gray-800 text-white" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateTaskForm;
